import Modal from "@common/Modal";
import { useEffect, useRef, useState } from "react";
import * as HELPER from "../../../config/helper";
import { useLanguage } from "@src/context/LanguageContext";
import { useSelector } from "react-redux";

interface AddOn {
  id?: number;
  name: string;
  premium_amount: number;
}

interface AddonsByTenure {
  tenure?: number;
  tenure_label?: string;
  insurance_type_name?: string;
  basic_premium_amount?: number;
  basic_addon_amount?: number;
  special_addon_amount?: number;
  total_addon_amount?: number;
  total_premium_amount?: number;
  insurance_start_date?: string;
  insurance_end_date?: string;
  addon_list?: AddOn[];
  note?: string;
}

interface FinalQuotationData {
  insurer_name: string;
  insurance_type_name: string;
  car_fmv: number;
  acc_price: number;
  total_fmv: number;
  basic_premium_amount: number;
  total_addon_amount: number;
  final_premium: number;
  total_premium_amount : number,
  admin_fee: number;
  customer_discount: number;
  total_invoice: number;
  agent_comission_amount: number;
  agent_point_amount: number;
  addon_list: AddOn[];
  addons_by_tenure?: AddonsByTenure[];
  insurance_start_date?: string;
  insurance_end_date?: string;
}

interface FinalQuotationPopupProps {
    show: boolean;
    handleClose: () => void;
    onConfirm: () => void;
    goToNextStep:() =>  void;
    finalQuotationData:FinalQuotationData,
    isRuleEngine:boolean,
    requestData: any
}

export default function FinalQuotationPopup({
    show,
    handleClose,
    onConfirm,
    goToNextStep,
    finalQuotationData,
    isRuleEngine,
    requestData
}: FinalQuotationPopupProps) {
    const [showAddons, setShowAddons] = useState(true);
    const [expandedAddons, setExpandedAddons] = useState<Record<string, boolean>>({});
    const [openApplicableInfoId, setOpenApplicableInfoId] = useState<string | null>(null);
    const {translations} = useLanguage();
    const applicableInfoRef = useRef<HTMLDivElement | null>(null);
    const commonState = useSelector((state: any) => state.common)?.common
    const usageType:string = commonState?.vehicle_usage?.find((row:any)=>row?.value===requestData.vehicle_usage)?.label
    const tenureWiseAddons = Array.isArray(finalQuotationData?.addons_by_tenure) ? finalQuotationData.addons_by_tenure : [];
    const hasTenureWiseAddons = tenureWiseAddons.length > 0;

    const formatQuotationDate = (date?: string) => {
        if (!date) return "";
        const parsedDate = new Date(date);
        if (Number.isNaN(parsedDate.getTime())) return "";
        return HELPER.formatDate(parsedDate, "dd mon, yyyy");
    };

    const getDateRange = (startDate?: string, endDate?: string) => {
        const formattedStartDate = formatQuotationDate(startDate);
        const formattedEndDate = formatQuotationDate(endDate);
        if (!formattedStartDate && !formattedEndDate) return "";
        if (!formattedStartDate) return formattedEndDate;
        if (!formattedEndDate) return formattedStartDate;
        return `${formattedStartDate} - ${formattedEndDate}`;
    };

    const getTenureKey = (tenureData: AddonsByTenure, index: number) => `${tenureData?.tenure ?? index}-${index}`;
    const getTenureAddonAmount = (tenureData: AddonsByTenure) =>
        tenureData?.total_addon_amount ?? Number(tenureData?.basic_addon_amount || 0) + Number(tenureData?.special_addon_amount || 0);
    const insuranceTypeLabels = (finalQuotationData.insurance_type_name || tenureWiseAddons[0]?.insurance_type_name || "")
        .split(",")
        .map((label) => label.trim())
        .filter(Boolean);
    const overallPeriod = getDateRange(
        finalQuotationData?.insurance_start_date || tenureWiseAddons[0]?.insurance_start_date,
        finalQuotationData?.insurance_end_date || tenureWiseAddons[tenureWiseAddons.length - 1]?.insurance_end_date
    );


    useEffect(() => {
        if (show) document.body.classList.add("overflow-hidden");
        return () => {
            document.body.classList.remove("overflow-hidden");
        }
    }, [show])

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (
                applicableInfoRef.current &&
                !applicableInfoRef.current.contains(event.target as Node)
            ) {
                setOpenApplicableInfoId(null);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    useEffect(() => {
        if (!show) return;
        setShowAddons(true);
        setOpenApplicableInfoId(null);
        setExpandedAddons(
            tenureWiseAddons.reduce((acc, tenureData, index) => {
                acc[getTenureKey(tenureData, index)] = true;
                return acc;
            }, {} as Record<string, boolean>)
        );
    }, [show, finalQuotationData?.addons_by_tenure])

    return (
        <div className="model-outer final-quotation-modal-wrapper">
            <Modal show={show} handleClose={handleClose}>
                

                {/* Header */}
                <div className="modal-header">
                    <h2>{translations?.CREATE_APP?.FINAL_QUOTE}</h2>
                </div>

                {/* Body */}
                <div className="modal-body">
                    <div className="fq-row"><span>{translations?.CREATE_APP?.INSURANCE_COMPANY}</span><label>{finalQuotationData.insurer_name}</label></div>
                    <div className="fq-row">
                        <span>{translations?.CREATE_APP?.TYPE_OF_INSURANCE}</span>
                        <label>
                            {insuranceTypeLabels.length > 1 ? (
                                insuranceTypeLabels.map((label, index) => (
                                    <span key={`${label}-${index}`} style={{ display: "block" }}>
                                        {label}
                                    </span>
                                ))
                            ) : (
                                finalQuotationData.insurance_type_name || tenureWiseAddons[0]?.insurance_type_name
                            )}
                        </label>
                    </div>
                    <div className="fq-row"><span>{translations?.CREATE_APP?.USAGE_TYPE}</span><label>{usageType}</label></div>
                    {overallPeriod && <div className="fq-row"><span>{translations?.CREATE_APP?.PERIOD || "Period"}</span><label>{overallPeriod}</label></div>}
                    <div className="fq-row"><span>{translations?.CREATE_APP?.ESTIMATED_TSI}</span><label>{HELPER.addCurrencyFormatting(finalQuotationData.car_fmv)}</label></div>
                    <div className="fq-row"><span>{translations?.CREATE_APP?.PRICE_OF_ACC}</span><label>{HELPER.addCurrencyFormatting(finalQuotationData.acc_price)}</label></div>
                    <div className="fq-row br-bottom-none"><span>{translations?.CREATE_APP?.OVERALL_CAR_PRICE}</span><label>{HELPER.addCurrencyFormatting(finalQuotationData.total_fmv)}</label></div>

                    <div className="fq-wave-divider"></div>

                    {hasTenureWiseAddons ? (
                        tenureWiseAddons.map((tenureData, index) => {
                            const tenureKey = getTenureKey(tenureData, index);
                            const addonAmount = getTenureAddonAmount(tenureData);
                            const showTenureAddons = expandedAddons[tenureKey] ?? true;
                            const tenureDateRange = getDateRange(tenureData?.insurance_start_date, tenureData?.insurance_end_date);

                            return (
                                <div key={tenureKey} className="fq-tenure-section">
                                    <div className="fq-tenure-header">
                                        <span>{tenureData?.tenure_label || tenureData?.insurance_type_name || finalQuotationData.insurance_type_name}</span>
                                        {tenureDateRange && <label>{tenureDateRange}</label>}
                                    </div>

                                    {tenureData?.note && (
                                        <div
                                            className="applicable-info-wrap fq-applicable-info"
                                            ref={openApplicableInfoId === tenureKey ? applicableInfoRef : null}
                                        >
                                            <span className="applicable-txt">
                                                {tenureData.note}
                                                <button
                                                    type="button"
                                                    className="applicable-info-btn"
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        setOpenApplicableInfoId((prev) => (prev === tenureKey ? null : tenureKey));
                                                    }}
                                                >
                                                    <i className="ic-info-icon1 addon-i-info"></i>
                                                </button>
                                            </span>

                                            {openApplicableInfoId === tenureKey && (
                                                <div className="applicable-info-popup">
                                                    {tenureData.note}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="fq-row br-bottom-none">
                                        <span>{translations?.CREATE_APP?.BASIC_PREMIUM}</span>
                                        <label>{HELPER.addCurrencyFormatting(tenureData?.basic_premium_amount)}</label>
                                    </div>

                                    <div
                                        className={`fq-addon-header ${!showTenureAddons ? "collapsed" : ""}`}
                                        onClick={() => setExpandedAddons((prev) => ({ ...prev, [tenureKey]: !showTenureAddons }))}
                                    >
                                        <div className="fq-addon-left">
                                            <span>{translations?.CREATE_APP?.ADD_ONS_PREMIUM}</span>&nbsp;&nbsp;&nbsp;
                                            <i className={`ic-arrow ${showTenureAddons ? "open" : ""}`}></i>
                                        </div>

                                        <label>{HELPER.addCurrencyFormatting(addonAmount)}</label>
                                    </div>

                                    {showTenureAddons && (
                                        <div className="fq-addon-body">
                                            {tenureData?.addon_list?.map((addon, addonIndex) => (
                                                <div key={`${addon.id || addon.name}-${addonIndex}`} className="fq-addon-row">
                                                    <span>• {addon.name}</span>
                                                    <label>{HELPER.addCurrencyFormatting(addon.premium_amount)}</label>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <>
                            <div className="fq-row br-bottom-none"><span>{translations?.CREATE_APP?.BASIC_PREMIUM}</span><label>{HELPER.addCurrencyFormatting(finalQuotationData.basic_premium_amount)}</label></div>
                            <div className={`fq-addon-header ${!showAddons ? "collapsed" : ""}`} onClick={() => setShowAddons(!showAddons)}>
                                <div className="fq-addon-left">
                                    <span>{translations?.CREATE_APP?.ADD_ONS_PREMIUM}</span>&nbsp;&nbsp;&nbsp;
                                    <i className={`ic-arrow ${showAddons ? "open" : ""}`}></i>
                                </div>

                                <label>{HELPER.addCurrencyFormatting(finalQuotationData.total_addon_amount)}</label>
                            </div>

                            {showAddons && (
                                <div className="fq-addon-body">
                                    {finalQuotationData.addon_list?.map((addon, addonIndex) => (
                                        <div key={`${addon.id || addon.name}-${addonIndex}`} className="fq-addon-row">
                                            <span>• {addon.name}</span>
                                            <label>{HELPER.addCurrencyFormatting(addon.premium_amount)}</label>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="fq-row br-bottom-none">
                                <span>{translations?.CREATE_APP?.TOTAL_PREMIUM}</span>
                                <label>{HELPER.addCurrencyFormatting(finalQuotationData.total_premium_amount)}</label>
                            </div>
                        </>
                    )}

                    {/* <div className="fq-wave-divider"></div> */}

                    <div className="fq-row total-premium-txt-qut">
                        <span>{translations?.CREATE_APP?.TOTAL_PREMIUM}</span>
                        <label>{HELPER.addCurrencyFormatting(finalQuotationData.total_premium_amount)}</label>
                    </div>
                    <div className="fq-row">
                        <span>{translations?.CREATE_APP?.DISCOUNT}</span>
                        <label>{HELPER.addCurrencyFormatting(finalQuotationData.customer_discount)}</label>
                    </div>

                    <div className="fq-row">
                        <span>{translations?.CREATE_APP?.ADMIN_FEE}</span>
                        <label>{HELPER.addCurrencyFormatting(finalQuotationData.admin_fee)}</label>
                    </div>

                    <div className="fq-row total">
                        <span>{translations?.CREATE_APP?.TOTAL_PAID}</span>
                        <label>{HELPER.addCurrencyFormatting(finalQuotationData.total_invoice)}</label>
                    </div>
                    <div className="fq-earning-box">
                        <div>
                            <span>{translations?.CREATE_APP?.COMMISSION_EARNED}</span>
                            <label>{HELPER.addCurrencyFormatting(finalQuotationData.agent_comission_amount)}</label>
                        </div>
                        <div className="t-right">
                            <span>{translations?.CREATE_APP?.POINTS_EARNED}</span>
                            <label>{HELPER.addCurrencyFormatting(finalQuotationData.agent_point_amount)}</label>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="modal-footer">
                    <button className="btn-line btn-share" onClick={onConfirm} disabled ={!isRuleEngine}>
                    <i className="ic-share"></i>
                    </button>
                    <button className="btn-primary" onClick={goToNextStep}>{translations?.COMMON_MSG?.CONTINUE}</button>
                </div>

                
            </Modal>
        </div>
    );
}
